<script lang="ts">
  import type { Snippet } from 'svelte';
  import { Icon } from '$ui/primitives/Icon';

  let {
    open = $bindable(false),
    title,
    description,
    size = 'md',
    dismissible = true,
    onclose,
    children,
    footer,
    class: className = ''
  }: {
    open?: boolean;
    title: string;
    description?: string;
    size?: 'sm' | 'md' | 'lg' | 'xl';
    dismissible?: boolean;
    onclose?: () => void;
    children: Snippet;
    footer?: Snippet;
    class?: string;
  } = $props();

  let mounted = $state(false);
  let closing = $state(false);
  let panelEl: HTMLElement | undefined = $state();
  let triggerEl: HTMLElement | null = null;

  const titleId = `modal-title-${Math.random().toString(36).slice(2, 9)}`;
  const descId = $derived(
    description ? `modal-desc-${Math.random().toString(36).slice(2, 9)}` : undefined
  );

  const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function getFocusables(): HTMLElement[] {
    if (!panelEl) return [];
    return Array.from(panelEl.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
      (el) => !el.hasAttribute('disabled') && el.offsetParent !== null
    );
  }

  function handleKeydown(e: KeyboardEvent) {
    if (!open) return;
    if (e.key === 'Escape' && dismissible) {
      e.preventDefault();
      requestClose();
      return;
    }
    if (e.key !== 'Tab') return;
    const focusables = getFocusables();
    if (focusables.length === 0) {
      e.preventDefault();
      return;
    }
    const first = focusables[0]!;
    const last = focusables[focusables.length - 1]!;
    const active = document.activeElement as HTMLElement | null;
    if (e.shiftKey) {
      if (active === first || !panelEl?.contains(active)) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (active === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  function onBackdropPointerDown(e: PointerEvent) {
    if (!dismissible) return;
    if (e.target === e.currentTarget) requestClose();
  }

  function requestClose() {
    if (closing) return;
    closing = true;
    window.setTimeout(() => {
      open = false;
      closing = false;
      onclose?.();
    }, 200);
  }

  let prevBodyOverflow = '';
  let nestedCount = $state(0);

  $effect(() => {
    if (open && !mounted) {
      mounted = true;
      closing = false;
      triggerEl = document.activeElement as HTMLElement | null;
      prevBodyOverflow = document.body.style.overflow;
      nestedCount = (window as unknown as { __sqModalCount?: number }).__sqModalCount ?? 0;
      (window as unknown as { __sqModalCount?: number }).__sqModalCount = nestedCount + 1;
      document.body.style.overflow = 'hidden';
      window.setTimeout(() => {
        const focusables = getFocusables();
        if (focusables.length > 0) {
          focusables[0]!.focus();
        } else {
          panelEl?.focus();
        }
      }, 20);
    } else if (!open && mounted) {
      // Only restore when no nested modal is still open.
      const cur = (window as unknown as { __sqModalCount?: number }).__sqModalCount ?? 0;
      const next = Math.max(0, cur - 1);
      (window as unknown as { __sqModalCount?: number }).__sqModalCount = next;
      if (next === 0) {
        document.body.style.overflow = prevBodyOverflow;
      }
      mounted = false;
      if (triggerEl && document.body.contains(triggerEl)) {
        window.setTimeout(() => triggerEl?.focus(), 20);
      }
    }
  });

  $effect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => handleKeydown(e);
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  });

  // Defensive cleanup: if the modal is unmounted while still open (e.g. a
  // theme change replaces the component tree before the close branch runs),
  // release the overflow lock so the page stays scrollable.
  $effect(() => {
    return () => {
      if (mounted) {
        const cur = (window as unknown as { __sqModalCount?: number }).__sqModalCount ?? 0;
        const next = Math.max(0, cur - 1);
        (window as unknown as { __sqModalCount?: number }).__sqModalCount = next;
        if (next === 0 && document.body.style.overflow === 'hidden') {
          document.body.style.overflow = '';
        }
      }
    };
  });
</script>

{#if mounted}
  <div
    class="backdrop"
    data-closing={closing || undefined}
    data-nested={nestedCount > 0 || undefined}
    onpointerdown={onBackdropPointerDown}
    role="presentation"
  >
    <div
      class="panel {className}"
      data-size={size}
      data-closing={closing || undefined}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descId}
      tabindex="-1"
      bind:this={panelEl}
    >
      <header class="head">
        <div class="head-texts">
          <h2 id={titleId} class="title">{title}</h2>
          {#if description}
            <p id={descId} class="description">{description}</p>
          {/if}
        </div>
        {#if dismissible}
          <button
            type="button"
            class="close"
            onclick={requestClose}
            aria-label="Cerrar"
          >
            <Icon name="x" size={16} />
          </button>
        {/if}
      </header>

      <div class="body">
        {@render children()}
      </div>

      {#if footer}
        <footer class="foot">
          {@render footer()}
        </footer>
      {/if}
    </div>
  </div>
{/if}

<style>
  .backdrop {
    position: fixed;
    inset: 0;
    z-index: var(--z-modal);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--space-6);
    background: var(--color-scrim);
    animation: backdrop-in var(--dur-slow) var(--ease-out);
  }

  .backdrop[data-nested] {
    background: var(--color-scrim-soft);
  }

  .backdrop[data-closing] {
    animation: backdrop-out var(--dur-fast) var(--ease-out) forwards;
  }

  .panel {
    display: flex;
    flex-direction: column;
    width: 100%;
    max-height: calc(100dvh - var(--space-8));
    background: var(--color-surface-elevated);
    border: var(--border-width) solid var(--color-border);
    border-radius: var(--radius-md);
    box-shadow: var(--shadow-lg);
    animation: panel-in var(--dur-slow) var(--ease-out);
  }

  .panel[data-closing] {
    animation: panel-out var(--dur-fast) var(--ease-out) forwards;
  }

  .panel[data-size='sm'] { max-width: 400px; }
  .panel[data-size='md'] { max-width: 480px; }
  .panel[data-size='lg'] { max-width: 640px; }
  .panel[data-size='xl'] { max-width: 800px; }

  .panel:focus {
    outline: none;
  }

  .head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: var(--space-3);
    padding: var(--space-5) var(--space-6) var(--space-4);
    border-bottom: var(--border-width) solid var(--color-border);
  }

  .head-texts {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    min-width: 0;
  }

  .title {
    margin: 0;
    font-family: var(--font-display);
    font-size: 1.5rem;
    font-weight: 700;
    letter-spacing: -0.02em;
    line-height: 1.2;
    color: var(--color-text-primary);
    text-wrap: balance;
  }

  .description {
    margin: 0;
    font-size: var(--text-sm);
    line-height: var(--text-sm-lh);
    color: var(--color-text-secondary);
  }

  .close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 32px;
    height: 32px;
    padding: 0;
    background: transparent;
    border: none;
    border-radius: var(--radius-sm);
    color: var(--color-text-muted);
    cursor: pointer;
    transition: background var(--dur-fast) var(--ease-out), color var(--dur-fast) var(--ease-out);
  }

  .close:hover {
    background: var(--color-surface-hover);
    color: var(--color-text-primary);
  }

  .close:focus-visible {
    outline: var(--focus-ring-width) solid var(--color-focus-ring);
    outline-offset: 2px;
  }

  .body {
    flex: 1;
    overflow-y: auto;
    padding: var(--space-5) var(--space-6);
  }

  .foot {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: var(--space-2);
    padding: var(--space-4) var(--space-6);
    border-top: var(--border-width) solid var(--color-border);
  }

  @keyframes backdrop-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes backdrop-out {
    from { opacity: 1; }
    to { opacity: 0; }
  }

  @keyframes panel-in {
    from { opacity: 0; transform: scale(0.96); }
    to { opacity: 1; transform: scale(1); }
  }

  @keyframes panel-out {
    from { opacity: 1; transform: scale(1); }
    to { opacity: 0; transform: scale(0.96); }
  }
</style>